class ConsentFormHubSpot extends HTMLElement {
  constructor() {
    super();
    this.maxRetries = 12;
    this.attempts = 0;
  }

  connectedCallback() {
    this.portalId = this.dataset.portalId;
    this.formId = this.dataset.formId;
    this.region = this.dataset.region || 'na1';
    this.target = this.querySelector('[data-hs-target]');

    if (!this.target || !this.portalId || !this.formId) {
      console.warn('[consent-form] Falta target, portalId o formId.');
      return;
    }

    this.load();
  }

  createForm() {
    try {
      window.hbspt.forms.create({
        region: this.region,
        portalId: this.portalId,
        formId: this.formId,
        target: '#' + this.target.id,
        onFormReady: (formArg) => {
          const formEl =
            formArg instanceof HTMLElement
              ? formArg
              : this.target.querySelector('form.hs-form');
          this.setupMultiStep(formEl);
        }
      });
    } catch (error) {
      console.error('[consent-form] Error al crear formulario:', error);
    }
  }

  setupMultiStep(formEl) {
    if (!formEl || formEl.dataset.cfMultistep === '1') return;

    const steps = [
      { title: 'Tus datos' },
      { title: 'Cuéntanos de ti' },
      { title: 'Confirmación' }
    ];

    const contactSel = '.hs_firstname, .hs_lastname, .hs_email, .hs_phone';
    const lastSel = '.legal-consent-container, .hs_submit';

    // Incluimos wrappers (p. ej. .hs-dependent-field) que contengan campos,
    // pero nunca los inputs ocultos ni el iframe de HubSpot.
    const blocks = Array.from(formEl.children).filter(
      (el) =>
        el.tagName === 'DIV' &&
        (el.classList.contains('hs-form-field') ||
          el.classList.contains('legal-consent-container') ||
          el.classList.contains('hs_submit') ||
          el.querySelector('.hs-form-field, .legal-consent-container'))
    );

    // Si el formulario no tiene la estructura esperada, no lo dividimos.
    if (blocks.length < 4) return;

    formEl.dataset.cfMultistep = '1';

    const stepFor = (el) => {
      if (el.matches(contactSel) || el.querySelector(contactSel)) return 0;
      if (el.matches(lastSel) || el.querySelector(lastSel)) return 2;
      return 1;
    };

    const stepsWrap = document.createElement('div');
    stepsWrap.className = 'cf-steps';

    const stepEls = steps.map((_, i) => {
      const d = document.createElement('div');
      d.className = 'cf-step';
      d.dataset.step = String(i);
      if (i !== 0) d.hidden = true;
      stepsWrap.appendChild(d);
      return d;
    });

    blocks.forEach((b) => stepEls[stepFor(b)].appendChild(b));

    // Identificadores estables para maquetar las preguntas sin depender
    // de los nombres generados por HubSpot ni alterar su configuración.
    Array.from(stepEls[1].children).forEach((block, index) => {
      block.classList.add('cf-question', `cf-question--${index + 1}`);
    });

    const checkboxBlock = stepEls[1].querySelector('.hs-fieldtype-checkbox');
    if (checkboxBlock) {
      checkboxBlock.closest('.cf-question')?.classList.add('cf-block--wide');
    }

    const progress = document.createElement('div');
    progress.className = 'cf-step-progress';
    progress.innerHTML = steps
      .map(
        (s, i) =>
          `<button type="button" class="cf-step-dot${
            i === 0 ? ' is-active' : ''
          }" data-dot="${i}"><span class="cf-step-dot__num">${
            i + 1
          }</span><span class="cf-step-dot__label">${s.title}</span></button>`
      )
      .join('');

    const nav = document.createElement('div');
    nav.className = 'cf-step-nav';
    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'cf-step-btn cf-step-prev';
    prevBtn.textContent = 'Atrás';
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'cf-step-btn cf-step-next';
    nextBtn.textContent = 'Siguiente';
    nav.appendChild(prevBtn);
    nav.appendChild(nextBtn);

    formEl.appendChild(progress);
    formEl.appendChild(stepsWrap);
    formEl.appendChild(nav);

    let current = 0;
    const total = steps.length;

    const validateStep = (idx) => {
      const fields = stepEls[idx].querySelectorAll('input, select, textarea');
      for (const f of fields) {
        if (f.willValidate && !f.checkValidity()) {
          if (typeof f.reportValidity === 'function') f.reportValidity();
          else f.focus();
          return false;
        }
      }
      return true;
    };

    const render = () => {
      stepEls.forEach((el, i) => {
        el.hidden = i !== current;
      });
      progress.querySelectorAll('.cf-step-dot').forEach((d, i) => {
        d.classList.toggle('is-active', i === current);
        d.classList.toggle('is-done', i < current);
      });
      prevBtn.hidden = current === 0;
      nextBtn.hidden = current === total - 1;
      const submit = formEl.querySelector('.hs_submit');
      if (submit) submit.classList.toggle('cf-hidden', current !== total - 1);
    };

    nextBtn.addEventListener('click', () => {
      if (!validateStep(current)) return;
      if (current < total - 1) {
        current++;
        render();
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    prevBtn.addEventListener('click', () => {
      if (current > 0) {
        current--;
        render();
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    progress.querySelectorAll('.cf-step-dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        const target = parseInt(dot.dataset.dot, 10);
        if (target < current) {
          current = target;
          render();
        }
      });
    });

    render();
  }

  load() {
    if (typeof window.hbspt !== 'undefined' && window.hbspt.forms) {
      this.createForm();
      return;
    }

    if (!document.getElementById('cf-hs-shell')) {
      const script = document.createElement('script');
      script.id = 'cf-hs-shell';
      script.src = 'https://js.hsforms.net/forms/embed/v2.js';
      script.defer = true;
      document.head.appendChild(script);
    }

    const poll = setInterval(() => {
      this.attempts++;
      if (typeof window.hbspt !== 'undefined' && window.hbspt.forms) {
        clearInterval(poll);
        this.createForm();
      } else if (this.attempts >= this.maxRetries) {
        clearInterval(poll);
        this.target.innerHTML =
          '<p class="cf-consent__fallback">El formulario no está disponible en este momento. Por favor, <a href="mailto:hola@adrianagallardo.com">contáctanos directamente</a>.</p>';
      }
    }, 600);
  }
}

if (!customElements.get('consent-form-hubspot')) {
  customElements.define('consent-form-hubspot', ConsentFormHubSpot);
}

class ScholarshipCountdown extends HTMLElement {
  constructor() {
    super();
    this.intervalId = null;
  }

  connectedCallback() {
    this.deadline = Date.parse(this.dataset.deadline || '');
    this.units = this.querySelector('[data-countdown-units]');
    this.endedMessage = this.querySelector('[data-countdown-ended]');
    this.values = {
      days: this.querySelector('[data-countdown-days]'),
      hours: this.querySelector('[data-countdown-hours]'),
      minutes: this.querySelector('[data-countdown-minutes]'),
      seconds: this.querySelector('[data-countdown-seconds]')
    };

    if (!Number.isFinite(this.deadline)) {
      console.warn('[scholarship-countdown] Fecha límite inválida.');
      return;
    }

    this.update();
    if (Date.now() < this.deadline) {
      this.intervalId = window.setInterval(() => this.update(), 1000);
    }
  }

  disconnectedCallback() {
    this.stop();
  }

  setValue(unit, value) {
    const element = this.values[unit];
    if (!element) return;
    const nextValue = String(value).padStart(2, '0');
    if (element.textContent !== nextValue) element.textContent = nextValue;
  }

  update() {
    const remaining = Math.max(0, this.deadline - Date.now());

    if (remaining <= 0) {
      this.finish();
      return;
    }

    this.setValue('days', Math.floor(remaining / 86400000));
    this.setValue('hours', Math.floor((remaining % 86400000) / 3600000));
    this.setValue('minutes', Math.floor((remaining % 3600000) / 60000));
    this.setValue('seconds', Math.floor((remaining % 60000) / 1000));
  }

  finish() {
    this.stop();
    this.classList.add('is-ended');
    if (this.units) this.units.hidden = true;
    if (this.endedMessage) this.endedMessage.hidden = false;
  }

  stop() {
    if (!this.intervalId) return;
    window.clearInterval(this.intervalId);
    this.intervalId = null;
  }
}

if (!customElements.get('scholarship-countdown')) {
  customElements.define('scholarship-countdown', ScholarshipCountdown);
}
